import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/dashboard/PageHeader';
import GettingStartedChecklist from '@/components/dashboard/GettingStartedChecklist';
import KpiCard from '@/components/dashboard/KpiCard';
import PipelineList from '@/components/dashboard/PipelineList';
import ActivityFeed from '@/components/dashboard/ActivityFeed';
import TodayScheduleCard from '@/components/dashboard/TodayScheduleCard';
import CrewClockCard from '@/components/dashboard/CrewClockCard';
import UnpaidInvoicesCard from '@/components/dashboard/UnpaidInvoicesCard';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  HardHat,
  Receipt,
  CircleCheck,
  AlertTriangle,
  RefreshCw,
  AlertCircle,
  Gauge,
  CalendarCheck,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { fetchDashboardData, formatCurrency } from '@/lib/dashboardData';
import { runPortalPhotoMigration } from '@/lib/portalPhotoMigration';
import { useAuth } from '@/context/AuthContext';

/* ----------------------------------------------------------------------------
 * Margin Health Pill — at-a-glance bucket of avg margin across active jobs.
 * Colors:
 *   healthy → green   (≥ 25%)
 *   watch   → amber   (≥ 10%)
 *   at_risk → rose    (< 10%)
 *   unknown → neutral (no active jobs with revenue)
 * ------------------------------------------------------------------------- */
const MARGIN_BUCKET_STYLES = {
  healthy: {
    dot: 'bg-[hsl(150_56%_50%)]',
    text: 'text-[hsl(150_56%_60%)]',
    border: 'border-[hsl(150_56%_50%)]/35',
    bg: 'bg-[hsl(150_56%_50%)]/10',
    labelKey: 'dashboard.posture.healthy',
  },
  watch: {
    dot: 'bg-[hsl(38_92%_60%)]',
    text: 'text-[hsl(38_92%_70%)]',
    border: 'border-[hsl(38_92%_60%)]/35',
    bg: 'bg-[hsl(38_92%_60%)]/10',
    labelKey: 'dashboard.posture.watch',
  },
  at_risk: {
    dot: 'bg-[hsl(354_70%_60%)]',
    text: 'text-[hsl(354_70%_70%)]',
    border: 'border-[hsl(354_70%_60%)]/35',
    bg: 'bg-[hsl(354_70%_60%)]/10',
    labelKey: 'dashboard.posture.at_risk',
  },
  unknown: {
    dot: 'bg-muted-foreground',
    text: 'text-muted-foreground',
    border: 'border-border',
    bg: 'bg-[hsl(var(--muted))]',
    labelKey: 'dashboard.posture.no_data',
  },
};

const MarginHealthPill = ({ marginHealth }) => {
  const { t } = useTranslation();
  const bucket = marginHealth?.bucket || 'unknown';
  const styles = MARGIN_BUCKET_STYLES[bucket] || MARGIN_BUCKET_STYLES.unknown;
  const sample = marginHealth?.sampleCount || 0;
  const pct = Number(marginHealth?.avgMarginPct || 0);
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs',
        styles.border,
        styles.bg
      )}
      data-testid="dashboard-margin-health-pill"
      role="status"
      aria-live="polite"
    >
      <Gauge className={cn('h-3.5 w-3.5', styles.text)} aria-hidden />
      <span className="text-muted-foreground hidden sm:inline">
        {t('dashboard.posture.avg_margin')}
      </span>
      <span className={cn('font-semibold tabular-nums', styles.text)}>
        {sample > 0 ? `${pct.toFixed(1)}%` : '—'}
      </span>
      <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', styles.dot)} />
      <span className={cn('uppercase tracking-[0.18em] text-[10px]', styles.text)}>
        {t(styles.labelKey)}
      </span>
      {sample > 0 ? (
        <span className="text-muted-foreground hidden md:inline">
          ·{' '}
          {t('dashboard.posture.count_jobs', {
            count: sample,
            label:
              sample === 1
                ? t('dashboard.posture.job_one')
                : t('dashboard.posture.job_many'),
          })}
        </span>
      ) : null}
    </div>
  );
};

const KpiSkeletons = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
    {Array.from({ length: 4 }).map((_, i) => (
      <Card key={i} className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-5 sm:p-6">
          <Skeleton className="h-3 w-24 bg-[hsl(var(--muted))]" />
          <Skeleton className="mt-3 h-8 w-32 bg-[hsl(var(--muted))]" />
          <Skeleton className="mt-2 h-3 w-40 bg-[hsl(var(--muted))]" />
        </CardContent>
      </Card>
    ))}
  </div>
);

const PipelineSkeleton = () => (
  <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
    <CardContent className="p-5 space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="rounded-md border border-border p-4 space-y-3">
          <Skeleton className="h-4 w-1/2 bg-[hsl(var(--muted))]" />
          <Skeleton className="h-3 w-1/3 bg-[hsl(var(--muted))]" />
          <Skeleton className="h-1.5 w-full bg-[hsl(var(--muted))]" />
        </div>
      ))}
    </CardContent>
  </Card>
);

const FeedSkeleton = () => (
  <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))] h-full">
    <CardContent className="p-5 space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-start gap-3">
          <Skeleton className="h-7 w-7 rounded-md bg-[hsl(var(--muted))]" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-2/3 bg-[hsl(var(--muted))]" />
            <Skeleton className="h-3 w-full bg-[hsl(var(--muted))]" />
          </div>
        </div>
      ))}
    </CardContent>
  </Card>
);

const TileListSkeleton = ({ rows = 4 }) => (
  <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))] h-full">
    <CardContent className="p-5 space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3 w-1/2 bg-[hsl(var(--muted))]" />
          <Skeleton className="h-3 w-1/3 bg-[hsl(var(--muted))]" />
        </div>
      ))}
    </CardContent>
  </Card>
);

export default function DashboardHomePage() {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const result = await fetchDashboardData();
      setData(result);
    } catch (e) {
      setError(e?.message || 'Failed to load dashboard data.');
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Silent one-shot: rehost any legacy data-URL portal photos into Supabase
    // Storage. Idempotent + sessionStorage-guarded; never blocks UI; failures
    // are logged to console.warn only.
    runPortalPhotoMigration().catch(() => {});
  }, [load]);

  const fullName =
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    user?.email?.split('@')[0] ||
    'team';
  const firstName = (fullName || 'team').split(' ')[0];

  return (
    <div data-testid="dashboard-home">
      <PageHeader
        eyebrow={t('dashboard.eyebrow')}
        title={t('dashboard.welcome', { name: firstName })}
        subtitle={t('dashboard.subtitle')}
        actions={
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {!loading && data?.marginHealth ? (
              <MarginHealthPill marginHealth={data.marginHealth} />
            ) : null}
            {isAdmin ? (
              <Button
                asChild
                variant="outline"
                size="sm"
                className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
                data-testid="dashboard-day-summary-button"
              >
                <Link to="/dashboard/day-summary">
                  <CalendarCheck size={14} className="mr-2" />
                  {t('daySummary.button')}
                </Link>
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={() => load({ silent: true })}
              disabled={loading || refreshing}
              className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="dashboard-refresh-button"
            >
              <RefreshCw
                size={14}
                className={`mr-2 ${refreshing ? 'animate-spin' : ''}`}
              />
              {t('common.refresh')}
            </Button>
          </div>
        }
      />

      {/* First-run self-guided checklist — hides itself once done or dismissed. */}
      <GettingStartedChecklist />

      {/* Crew Clock front and center — first thing on the phone in the
          morning (beta ask). Live who's-on-the-clock; taps through to the
          full clock page. */}
      <div className="mb-4 sm:mb-5">
        <CrewClockCard />
      </div>

      {error ? (
        <Alert
          className="mb-6 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="dashboard-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      {/* KPI Row */}
      {loading ? (
        <KpiSkeletons />
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4"
          data-testid="dashboard-kpis"
        >
          <KpiCard
            label={t('dashboard.kpi_active_jobs')}
            value={data?.kpis?.activeJobs ?? 0}
            Icon={HardHat}
            tone="primary"
            hint={t('dashboard.kpi_active_jobs_hint', {
              clients: data?.counts?.clients ?? 0,
              leads: data?.counts?.leads ?? 0,
            })}
            testId="kpi-active-jobs"
            to="/dashboard/active-jobs"
          />
          <KpiCard
            label={t('dashboard.kpi_total_invoiced')}
            value={formatCurrency(data?.kpis?.totalInvoiced ?? 0)}
            Icon={Receipt}
            tone="default"
            hint={t('dashboard.kpi_invoiced_hint', {
              count: data?.kpis?.invoiceCount ?? 0,
            })}
            testId="kpi-total-invoiced"
            to="/dashboard/invoices"
          />
          <KpiCard
            label={t('dashboard.kpi_collected')}
            value={formatCurrency(data?.kpis?.collected ?? 0)}
            Icon={CircleCheck}
            tone="success"
            hint={t('dashboard.kpi_collected_hint', {
              paid: data?.kpis?.paidCount ?? 0,
              outstanding: formatCurrency(data?.kpis?.outstanding ?? 0),
            })}
            testId="kpi-collected"
            to="/dashboard/payments"
          />
          <KpiCard
            label={t('dashboard.kpi_overdue')}
            value={formatCurrency(data?.kpis?.overdue ?? 0)}
            Icon={AlertTriangle}
            tone={data?.kpis?.overdue > 0 ? 'destructive' : 'default'}
            hint={
              data?.kpis?.overdue > 0
                ? t('dashboard.kpi_overdue_hint_some')
                : t('dashboard.kpi_overdue_hint_none')
            }
            testId="kpi-overdue"
            to="/dashboard/reminders"
          />
        </div>
      )}

      {/* Pipeline + Activity — wide screens (xl+) put both modules side by
          side instead of stacking, per the wide-screen dashboard pass. */}
      <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          {loading ? (
            <PipelineSkeleton />
          ) : (
            <PipelineList jobs={data?.pipeline || []} />
          )}
        </div>
        <div>
          {loading ? <FeedSkeleton /> : <ActivityFeed items={data?.feed || []} />}
        </div>
      </div>

      {/* Unpaid Invoices + Today's Schedule — both visible without
          scrolling on wide screens, mirroring the row above. */}
      <div className="mt-4 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          {loading ? (
            <TileListSkeleton rows={5} />
          ) : (
            <UnpaidInvoicesCard invoices={data?.unpaidInvoices || []} />
          )}
        </div>
        <div>
          {loading ? (
            <TileListSkeleton rows={4} />
          ) : (
            <TodayScheduleCard entries={data?.todaySchedule || []} />
          )}
        </div>
      </div>
    </div>
  );
}
